import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';

import { type ScreenType, useScreenType } from '@gitbook/spine-react';

/**
 * Where the agent is shown.
 *
 * - `dock`: the bar at the bottom of the content column, with the conversation in a window above
 *   its tab.
 * - `anchored`: a panel in the layout's right rail, which stays on screen from one page to the
 *   next and shares the rail with whatever side panel the page opens.
 */
export type AgentDockMode = 'dock' | 'anchored';

/**
 * Height the dock's window opens at, before the user has dragged its edge.
 */
export const AGENT_DOCK_WINDOW_DEFAULT_HEIGHT = 512;

/**
 * How short the window can be dragged: room for its header, a few lines of conversation and the
 * composer under them.
 */
export const AGENT_DOCK_WINDOW_MIN_HEIGHT = 320;

/**
 * How tall the window can be dragged, on a screen with the room for it.
 */
export const AGENT_DOCK_WINDOW_MAX_HEIGHT = 800;

/**
 * Kept clear between the top of the window and the top of the column it lives in, when the column
 * rather than the ceiling above is what limits it: the same gap the window keeps from the bar, and a
 * little of the page besides.
 */
const AGENT_DOCK_WINDOW_TOP_GAP = 12;

/** Width of the rail before the user has dragged its edge: 28rem, room for a chart. */
export const AGENT_RAIL_DEFAULT_WIDTH = 448;

/** The rail's former fixed width, which a side panel moved into it still fits. */
export const AGENT_RAIL_MIN_WIDTH = 352;

/** Past this the content column gives up too much; the rail is also never more than half the window. */
export const AGENT_RAIL_MAX_WIDTH = 800;

type AgentDockLayout = {
    mode: AgentDockMode;
    /** How tall the user last made the dock's window. Absent until they resize it. */
    windowHeight?: number;
    /** How wide the user last made the rail. Absent until they resize it. */
    railWidth?: number;
};

/**
 * Where the user last put the agent.
 *
 * Per browser rather than per organization: this is the shape of the workspace, which a user
 * expects to hold as they move between organizations — the same deal the sidebar's width and
 * collapsed state get. The splash screen reads this key directly as well, before a first paint that
 * happens long before the app knows which organization it is loading.
 *
 * An object rather than a bare mode, so that the rest of what the workspace remembers — the height
 * of the dock's window, whatever the rail comes to hold — sits under the same key.
 *
 * Stored, and so shared between the user's browser windows: anchoring the agent in one anchors it
 * in all of them.
 */
const agentDockLayoutAtom = atomWithStorage<AgentDockLayout>(
    'agentDockLayout',
    { mode: 'dock' },
    undefined,
    { getOnInit: true }
);

/**
 * Set while the dock has failed to render and is waiting to try again.
 *
 * Not stored: it says nothing about where the user wants the agent, only that for the moment there
 * is no agent to make room for. The layout reads it so a rail is not kept for a dock that is not
 * there — a column of nothing, on a page with no side panel to fill it — and the preference behind
 * it is untouched, so the rail is back the moment the dock is.
 */
const agentDockFailedAtom = atom(false);

/**
 * Report, for as long as the calling component is mounted, that the dock has failed to render.
 * Mounted by the dock's error fallback and nothing else.
 */
export function useMarkAgentDockFailed() {
    const setFailed = useSetAtom(agentDockFailedAtom);

    React.useEffect(() => {
        setFailed(true);
        return () => setFailed(false);
    }, [setFailed]);
}

/**
 * Where the agent actually goes, which is where the user put it for as long as the window can hold
 * the rail.
 *
 * The rail takes a fixed column beside the sidebar and the content, which is more than a tablet has
 * to spare — so anything narrower than a desktop falls back to the dock, and the preference is
 * picked up again on a wider screen rather than being rewritten.
 */
export function getEffectiveAgentDockMode(input: {
    preference: AgentDockMode;
    screenType: ScreenType;
}): AgentDockMode {
    return input.preference === 'anchored' && input.screenType === 'desktop' ? 'anchored' : 'dock';
}

/**
 * Where the agent is shown, and how to move it.
 */
export function useAgentDockLayout(): {
    /** Where the agent goes on this screen. */
    mode: AgentDockMode;
    /** Whether anchoring is worth offering at all, which is what the control follows. */
    canAnchor: boolean;
    setMode: (mode: AgentDockMode) => void;
} {
    const [layout, setLayout] = useAtom(agentDockLayoutAtom);
    const { screenType, isDesktop } = useScreenType();
    const failed = useAtomValue(agentDockFailedAtom);

    const setMode = React.useCallback(
        (mode: AgentDockMode) => setLayout((previous) => ({ ...previous, mode })),
        [setLayout]
    );

    return {
        // A dock that cannot render goes nowhere, and nothing should hold a place for it: reported
        // as the dock so the layout keeps no rail, while the preference waits for it to recover.
        mode: failed ? 'dock' : getEffectiveAgentDockMode({ preference: layout.mode, screenType }),
        canAnchor: isDesktop,
        setMode,
    };
}

/**
 * The height the window may actually have, given `room`: the space between the window's bottom
 * edge and the top of the column it lives in, which clips whatever sticks out of it.
 *
 * The room lowers the ceiling, never the floor. A column too short for the minimum gets a window
 * that is clipped rather than one too short to use.
 */
export function clampAgentDockWindowHeight(height: number, room: number = Infinity): number {
    const ceiling = Math.min(AGENT_DOCK_WINDOW_MAX_HEIGHT, room - AGENT_DOCK_WINDOW_TOP_GAP);
    return Math.max(AGENT_DOCK_WINDOW_MIN_HEIGHT, Math.min(height, ceiling));
}

/**
 * How tall the user last made the dock's window, and a way to remember the next height.
 *
 * Stored as dragged rather than as it fits: what fits is the screen's business, and the window asks
 * the screen every time it renders.
 */
export function useAgentDockWindowHeight(): [number, (height: number) => void] {
    const [layout, setLayout] = useAtom(agentDockLayoutAtom);

    const setHeight = React.useCallback(
        (windowHeight: number) => setLayout((previous) => ({ ...previous, windowHeight })),
        [setLayout]
    );

    return [layout.windowHeight ?? AGENT_DOCK_WINDOW_DEFAULT_HEIGHT, setHeight];
}

/**
 * The rail's width and a way to change it. Read by the splash screen too, from the same key.
 */
export function useAgentRailWidth(): [number, (width: number) => void] {
    const [layout, setLayout] = useAtom(agentDockLayoutAtom);

    const setWidth = React.useCallback(
        (railWidth: number) => setLayout((previous) => ({ ...previous, railWidth })),
        [setLayout]
    );

    return [layout.railWidth ?? AGENT_RAIL_DEFAULT_WIDTH, setWidth];
}

/**
 * A stretch of the screen, left to right in viewport pixels.
 */
export type AgentDockBounds = { left: number; right: number };

/**
 * Where the open conversation is on screen, or null while none is showing.
 *
 * Reported by whichever surface is showing one — the dock's window or the anchored panel — for
 * what has to keep out of the agent's way: the toasts stack in the very corner the window opens
 * over, and would land on top of it. Only the horizontal extent, since both surfaces reach the
 * bottom of the screen, which is where the toasts are.
 */
const agentDockBoundsAtom = atom<AgentDockBounds | null>(null);

export function useAgentDockBounds(): AgentDockBounds | null {
    return useAtomValue(agentDockBoundsAtom);
}

/**
 * A way to report where the conversation is on screen, for as long as the calling component is
 * mounted: whatever it last reported is withdrawn when it unmounts.
 *
 * Layout-timed, like the measuring that feeds it, so that a surface replacing the other in one
 * commit has the old report withdrawn before it makes its own rather than after.
 */
export function useReportAgentDockBounds(): (bounds: AgentDockBounds | null) => void {
    const setBounds = useSetAtom(agentDockBoundsAtom);

    React.useLayoutEffect(() => () => setBounds(null), [setBounds]);

    return React.useCallback(
        (bounds: AgentDockBounds | null) =>
            setBounds((previous) => (isSameBounds(previous, bounds) ? previous : bounds)),
        [setBounds]
    );
}

function isSameBounds(a: AgentDockBounds | null, b: AgentDockBounds | null): boolean {
    return a === b || (a !== null && b !== null && a.left === b.left && a.right === b.right);
}
